/**
 * General Audit Log Page
 * Visar alla ändringar (CREATE, UPDATE, DELETE) i systemet
 * 
 * Refaktorerad att använda useAsyncData och usePagination
 */

import { useState, useCallback, useEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { ArrowLeftIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { usePagination } from '@/hooks';
import {
  generalAuditLogService,
  type AuditLogEntry,
  type AuditLogFilters,
  type AuditLogStatistics,
} from '@/services/generalAuditLog/generalAuditLogService';
const GeneralAuditLog = () => {
 const navigate = useNavigate();
 const { t } = useTranslation();
 const [filters, setFilters] = useState<AuditLogFilters>({
 entityType: '',
 action: undefined,
 search: '',
 });

 const pagination = usePagination({ initialPageSize: 100 });
 const [logs, setLogs] = useState<AuditLogEntry[]>([]);
 const [statistics, setStatistics] = useState<AuditLogStatistics | null>(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
  void generalAuditLogService
    .getStatistics()
    .then((res) => setStatistics(res.data))
    .catch(() => setStatistics(null));
 }, []);

 const loadLogs = useCallback(async () => {
  setLoading(true);
  try {
   const res = await generalAuditLogService.getAuditLog(
    filters,
    pagination.pageSize,
    pagination.offset
   );
   setLogs(res.data);
   pagination.setTotal(res.pagination.total);
  } catch {
   setLogs([]);
   pagination.setTotal(0);
  } finally {
   setLoading(false);
  }
 }, [filters, pagination.page, pagination.pageSize, pagination.offset]);

 useEffect(() => {
  void loadLogs();
 }, [loadLogs]);
 const handleFilterChange = useCallback((key: keyof AuditLogFilters, value: any) => {
 setFilters(prev => ({ ...prev, [key]: value }));
 pagination.firstPage();
 }, [pagination]);

 const getActionBadgeClass = (action: string) => {
 const classes: Record<string, string> = {
 CREATE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
 UPDATE: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
 DELETE: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
 };
 return classes[action] || 'bg-bgBase text-textDefault';
 };

 const formatDate = (dateString: string) => {
 return new Date(dateString).toLocaleString('sv-SE', {
 year: 'numeric',
 month: '2-digit',
 day: '2-digit',
 hour: '2-digit',
 minute: '2-digit',
 second: '2-digit',
 });
 };

 // Plocka ut de ändrade fälten oavsett action.
 // UPDATE => changes ({ field: { old, new } }), CREATE => new_values, DELETE => old_values.
 const getChangeDetails = (log: AuditLogEntry): { count: number; data: Record<string, any> } | null => {
 if (log.changes && Object.keys(log.changes).length > 0) {
 return { count: Object.keys(log.changes).length, data: log.changes };
 }
 if (Array.isArray(log.changed_fields) && log.changed_fields.length > 0) {
 return { count: log.changed_fields.length, data: log.changed_fields as unknown as Record<string, any> };
 }
 if (log.action === 'CREATE' && log.new_values && Object.keys(log.new_values).length > 0) {
 return { count: Object.keys(log.new_values).length, data: log.new_values };
 }
 if (log.action === 'DELETE' && log.old_values && Object.keys(log.old_values).length > 0) {
 return { count: Object.keys(log.old_values).length, data: log.old_values };
 }
 return null;
 };

 return (
 <div className="p-6 max-w-7xl mx-auto">
 <div className="mb-6">
 <div className="flex items-center gap-3">
 <button onClick={() => navigate(ROUTES.SYSADMIN)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
 <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
 </button>
 <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
 <ClipboardDocumentListIcon className="w-6 h-6 text-primary" />
 {t('sysAdmin.generalAuditLogSection.title')}
 </h1>
 </div>
 <p className="mt-2 text-sm text-textSecondary">
 {t('sysAdmin.generalAuditLogSection.description')}
 </p>
 </div>

 {/* Statistik */}
 {statistics && (
 <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">{t('sysAdmin.generalAuditLogSection.total')}</div>
 <div className="text-2xl font-bold text-textDefault">{statistics.total}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">CREATE</div>
 <div className="text-2xl font-bold text-green-600">{statistics.byAction?.CREATE || 0}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">UPDATE</div>
 <div className="text-2xl font-bold text-blue-600">{statistics.byAction?.UPDATE || 0}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">DELETE</div>
 <div className="text-2xl font-bold text-red-600">{statistics.byAction?.DELETE || 0}</div>
 </div>
 </div>
 )}

 {/* Filter */}
 <div className="mb-6 bg-bgElevated p-4 rounded-lg shadow">
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('sysAdmin.generalAuditLogSection.entityTypeLabel')}</label>
 <input
 type="text"
 value={filters.entityType || ''}
 onChange={(e) => handleFilterChange('entityType', e.target.value)}
 placeholder={t('sysAdmin.generalAuditLogSection.entityTypePlaceholder')}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('sysAdmin.generalAuditLogSection.actionLabel')}</label>
 <select
 value={filters.action || ''}
 onChange={(e) => handleFilterChange('action', e.target.value || undefined)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 >
 <option value="">{t('sysAdmin.generalAuditLogSection.allActions')}</option>
 <option value="CREATE">CREATE</option>
 <option value="UPDATE">UPDATE</option>
 <option value="DELETE">DELETE</option>
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('sysAdmin.generalAuditLogSection.searchLabel')}</label>
 <input
 type="text"
 value={filters.search || ''}
 onChange={(e) => handleFilterChange('search', e.target.value)}
 placeholder={t('sysAdmin.generalAuditLogSection.searchPlaceholder')}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 />
 </div>
 <div className="flex items-end">
 <button
 onClick={() => {
 setFilters({ entityType: '', action: undefined, search: '' });
 pagination.firstPage();
 }}
 className="w-full px-4 py-2 bg-bgBase text-textDefault rounded-md hover:bg-bgBase"
 >
 {t('sysAdmin.generalAuditLogSection.clearFilters')}
 </button>
 </div>
 </div>
 </div>

 {/* Loggar */}
 <div className="bg-bgElevated rounded-lg shadow overflow-hidden">
 {loading ? (
 <div className="p-6 text-center text-textSecondary">{t('sysAdmin.generalAuditLogSection.loading')}</div>
 ) : logs.length === 0 ? (
 <div className="p-6 text-center text-textSecondary">{t('sysAdmin.generalAuditLogSection.noChanges')}</div>
 ) : (
 <>
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-bgContainer">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('sysAdmin.generalAuditLogSection.colTime')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('sysAdmin.generalAuditLogSection.colUser')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('sysAdmin.generalAuditLogSection.colAction')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('sysAdmin.generalAuditLogSection.colEntity')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{t('sysAdmin.generalAuditLogSection.colChanges')}</th>
 </tr>
 </thead>
 <tbody className="bg-bgElevated divide-y divide-border">
 {logs.map((log) => (
 <tr key={log.id} className="hover:bg-bgBase">
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 {formatDate(log.created_at)}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 <div>
 <div className="font-medium">{log.username || t('sysAdmin.generalAuditLogSection.system')}</div>
 {log.user_email && (
 <div className="text-xs text-textSecondary">{log.user_email}</div>
 )}
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getActionBadgeClass(log.action)}`}>
 {log.action}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-textDefault">
 <div>
 <div className="font-medium">{log.entity_type}</div>
 <div className="text-xs text-textSecondary">{log.entity_name || log.entity_id}</div>
 </div>
 </td>
 <td className="px-6 py-4 text-sm text-textDefault">
 {(() => {
 const details = getChangeDetails(log);
 return details ? (
 <details className="cursor-pointer">
 <summary className="text-blue-600 dark:text-blue-400 hover:underline">
 {t('sysAdmin.generalAuditLogSection.fieldsCount').replace('{count}', String(details.count))}
 </summary>
 <pre className="mt-2 text-xs bg-bgLight p-2 rounded overflow-auto max-h-40 max-w-md">
 {JSON.stringify(details.data, null, 2)}
 </pre>
 </details>
 ) : (
 <span className="text-textTertiary">-</span>
 );
 })()}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 {/* Paginering */}
 {pagination.total > pagination.pageSize && (
 <div className="px-6 py-4 border-t border-border flex items-center justify-between">
 <div className="text-sm text-textDefault">
 {t('sysAdmin.generalAuditLogSection.showing').replace('{from}', String(pagination.startIndex + 1)).replace('{to}', String(pagination.endIndex)).replace('{total}', String(pagination.total))}
 </div>
 <div className="flex gap-2">
 <button
 onClick={pagination.previousPage}
 disabled={!pagination.hasPrevious}
 className="px-4 py-2 bg-bgBase text-textDefault rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bgBase"
 >
 {t('sysAdmin.generalAuditLogSection.previous')}
 </button>
 <button
 onClick={pagination.nextPage}
 disabled={!pagination.hasNext}
 className="px-4 py-2 bg-bgBase text-textDefault rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bgBase"
 >
 {t('sysAdmin.generalAuditLogSection.next')}
 </button>
 </div>
 </div>
 )}
 </>
 )}
 </div>
 </div>
 );
};

export default GeneralAuditLog;
